import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAdminAuth } from "../../contexts/AdminAuthContext";
import { useHistory, useLocation, Link } from "react-router-dom";
import { fetchAuthSession } from "aws-amplify/auth";
import {
  registerQuickSightUser,
  deleteQuickSightUser,
  inviteUserToDashboard,
  removeUserFromDashboard,
  setDashboardAsDefault,
  getUserDashboards,
  getCognitoUsersWithQuickSightStatus,
  createDashboardMetadata,
  listDashboardMetadata,
  updateDashboardMetadata,
  deleteDashboardMetadata,
} from "@/services/quicksight";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Users,
  Search,
  RefreshCw,
  LogOut,
  UserPlus,
  Shield,
  Trash2,
  BarChart2,
  Link2,
  UserX,
  Settings,
  Mail,
  Plus,
  Edit,
  Database,
  ChevronDown,
  Check,
  X,
  BarChart3,
} from "lucide-react";

const AdminQuickSight = () => {
  const { adminUser, adminLogout } = useAdminAuth();
  const [adminEmail, setAdminEmail] = useState("");
  const history = useHistory();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState("");
  const [cognitoUsers, setCognitoUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [dashboards, setDashboards] = useState([]);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [expandedUser, setExpandedUser] = useState(null);
  const [expandAllUsers, setExpandAllUsers] = useState(false);
  const [loadingUserDashboards, setLoadingUserDashboards] = useState({});
  const [userDashboards, setUserDashboards] = useState({});
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ selectedDashboards: [], defaultDashboardId: null, companyName: "" });
  const [inviteResult, setInviteResult] = useState(null);
  const [registeringUser, setRegisteringUser] = useState(false);
  const [invitingUser, setInvitingUser] = useState(false);
  

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownSearchTerm, setDropdownSearchTerm] = useState("");

  const [dashboardMetadata, setDashboardMetadata] = useState([]);
  const [metadataLoading, setMetadataLoading] = useState(false);
  const [metadataModalOpen, setMetadataModalOpen] = useState(false);
  const [editingMetadata, setEditingMetadata] = useState(null);
  const [metadataForm, setMetadataForm] = useState({
    dashboardId: "",
    dashboardName: "",
    dashboardLink: "",
    description: "",
  });

  useEffect(() => {
    (async () => {
      try {
        const session = await fetchAuthSession();
        const emailClaim = session.tokens.idToken.payload.email;
        setAdminEmail(emailClaim);
      } catch (err) {
        console.warn("Could not fetch admin email:", err);
      }
    })();
  }, []);

  const fetchCognitoUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const users = await getCognitoUsersWithQuickSightStatus();
      setCognitoUsers(users);
    } catch (err) {
      setError("Failed to load Cognito users. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCognitoUsers();
  }, []);

  const filteredUsers = cognitoUsers.filter((user) =>
    user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Show all users including admins, but mark them appropriately
  const visibleUsers = filteredUsers;

  const handleLogout = async () => {
    await adminLogout();
    history.push("/admin/login");
  };

  const handleRefresh = async () => {
    await fetchCognitoUsers();
  };

  const handleDeleteUser = async (userEmail) => {
    if (window.confirm(`Are you sure you want to delete QuickSight user ${userEmail}?`)) {
      try {
        await deleteQuickSightUser(userEmail);
        await fetchCognitoUsers();
      } catch (err) {
        setError(err.message || "Failed to delete user");
      }
    }
  };

  const handleRegisterUser = async (userEmail) => {
    if (!userEmail) {
      setError("Please select a user to register");
      return;
    }
    
    try {
      setRegisteringUser(true);
      setError(null);
      await registerQuickSightUser(userEmail);
      await fetchCognitoUsers();
    } catch (err) {
      console.error("Full error details:", err);
      // Display detailed error information
      let errorMessage = err.message || "Failed to register user";
      if (err.reason) {
        errorMessage += `\nReason: ${err.reason}`;
      }
      if (err.suggestion) {
        errorMessage += `\nSuggestion: ${err.suggestion}`;
      }
      if (err.userEmail) {
        errorMessage += `\nUser Email: ${err.userEmail}`;
      }
      if (err.missing) {
        errorMessage += `\nMissing Environment Variables: ${JSON.stringify(err.missing)}`;
      }
      setError(errorMessage);
    } finally {
      setRegisteringUser(false);
    }
  };

  const handleShowDashboards = async (user) => {
    if (expandedUser === user.email) {
      setExpandedUser(null);
      return;
    }
    setExpandedUser(user.email);
    setSelectedUser(user);
    if (userDashboards[user.email]) return;
    setDashboardLoading(true);
    try {
      const data = await getUserDashboards(user.email);
      setUserDashboards(prev => ({ ...prev, [user.email]: data }));
      setDashboards(data);
    } catch (err) {
      setError("Failed to load dashboards for user");
    } finally {
      setDashboardLoading(false);
    }
  };

  const handleShowAllDashboards = async () => {
    if (expandAllUsers) {
      setExpandAllUsers(false);
      return;
    }
    setExpandAllUsers(true);

    const registeredUsers = visibleUsers.filter(u => u.existsInQuickSight && !userDashboards[u.email]);
    const loadingState = {};
    registeredUsers.forEach(u => { loadingState[u.email] = true; });
    setLoadingUserDashboards(loadingState);

    await Promise.all(registeredUsers.map(async (user) => {
      try {
        const data = await getUserDashboards(user.email);
        setUserDashboards(prev => ({ ...prev, [user.email]: data }));
      } catch (err) {
        setUserDashboards(prev => ({ ...prev, [user.email]: [] }));
      } finally {
        setLoadingUserDashboards(prev => ({ ...prev, [user.email]: false }));
      }
    }));
  };

  const handleInviteToDashboard = async (user) => {
    setSelectedUser(user);
    setInviteModalOpen(true);
    setInviteForm({ selectedDashboards: [], defaultDashboardId: null, companyName: "" });
    setInviteResult(null);
  };

  const handleInviteSubmit = async (e) => {
    e.preventDefault();
    try {
      setInvitingUser(true);
      setInviteResult(null);
      
      // Validate that at least one dashboard is selected
      if (inviteForm.selectedDashboards.length === 0) {
        setInviteResult({ error: "Please select at least one dashboard" });
        return;
      }
      
      // Process each selected dashboard
      const results = [];
      const errors = [];
      
      for (let i = 0; i < inviteForm.selectedDashboards.length; i++) {
        const dashboardId = inviteForm.selectedDashboards[i];
        const selectedMetadata = dashboardMetadata.find(d => d.id === dashboardId);
        
        if (!selectedMetadata) {
          errors.push(`Dashboard with ID ${dashboardId} not found`);
          continue;
        }
        
        try {
          // Check if this dashboard should be set as default
          const makeDefault = inviteForm.defaultDashboardId === dashboardId;
          
          const result = await inviteUserToDashboard({
            userEmail: selectedUser.email,
            dashboardId: selectedMetadata.dashboardId,
            dashboardName: selectedMetadata.dashboardName,
            dashboardLink: selectedMetadata.dashboardLink,
            makeDefault: makeDefault,
            companyName: inviteForm.companyName,
          });
          
          results.push({
            dashboardName: selectedMetadata.dashboardName,
            success: true,
            result
          });
        } catch (err) {
          errors.push(`${selectedMetadata.dashboardName}: ${err.message || "Failed to invite"}`);
          results.push({
            dashboardName: selectedMetadata.dashboardName,
            success: false,
            error: err.message || "Failed to invite"
          });
        }
      }
      
      // Set result with summary
      const successCount = results.filter(r => r.success).length;
      const errorCount = results.filter(r => !r.success).length;
      
      setInviteResult({
        success: successCount > 0,
        message: `Successfully assigned ${successCount} dashboard(s)${errorCount > 0 ? `, ${errorCount} failed` : ''}`,
        results,
        errors
      });
      
      // Close modal if all succeeded
      if (errorCount === 0) {
      setInviteModalOpen(false);
      setSelectedUser(null);
      }
    } catch (err) {
      setInviteResult({ error: err.message || "Failed to invite user" });
    } finally {
      setInvitingUser(false);
    }
  };

  const refreshUserDashboards = async (user) => {
    try {
      const data = await getUserDashboards(user.email);
      setUserDashboards(prev => ({ ...prev, [user.email]: data }));
      setDashboards(data);
    } catch (err) {
      setError("Failed to refresh dashboards");
    }
  };

  const handleRemoveFromDashboard = async (dashboard) => {
    if (!selectedUser) return;
    if (window.confirm(`Remove user ${selectedUser.email} from dashboard ${dashboard.name}?`)) {
      try {
        await removeUserFromDashboard({
          userEmail: selectedUser.email,
          dashboardId: dashboard.quicksightDashboardId,
          viewId: dashboard.id,
        });
        await refreshUserDashboards(selectedUser);
      } catch (err) {
        setError("Failed to remove user from dashboard");
      }
    }
  };

  const handleSetAsDefault = async (dashboard) => {
    if (!selectedUser) return;
    if (window.confirm(`Set ${dashboard.name} as the default dashboard for ${selectedUser.email}?`)) {
      try {
        await setDashboardAsDefault({
          userEmail: selectedUser.email,
          dashboardId: dashboard.quicksightDashboardId,
          viewId: dashboard.id,
        });
        await refreshUserDashboards(selectedUser);
      } catch (err) {
        setError("Failed to set dashboard as default");
      }
    }
  };


  const fetchDashboardMetadata = async () => {
    try {
      setMetadataLoading(true);
      const metadata = await listDashboardMetadata();
      setDashboardMetadata(metadata);
    } catch (err) {
      setError("Failed to load dashboards");
    } finally {
      setMetadataLoading(false);
    }
  };

  const handleCreateMetadata = () => {
    setEditingMetadata(null);
    setMetadataForm({
      dashboardId: "",
      dashboardName: "",
      dashboardLink: "",
      description: "",
    });
    setMetadataModalOpen(true);
  };

  const handleEditMetadata = (metadata) => {
    setEditingMetadata(metadata);
    setMetadataForm({
      dashboardId: metadata.dashboardId,
      dashboardName: metadata.dashboardName,
      dashboardLink: metadata.dashboardLink,
      description: metadata.description || "",
    });
    setMetadataModalOpen(true);
  };

  const handleMetadataSubmit = async (e) => {
    e.preventDefault();
    
    // Check for duplicate dashboard ID (only for new dashboards)
    if (!editingMetadata) {
      const existingDashboard = dashboardMetadata.find(
        metadata => metadata.dashboardId.toLowerCase() === metadataForm.dashboardId.toLowerCase()
      );
      
      if (existingDashboard) {
        setError(`Dashboard ID "${metadataForm.dashboardId}" already exists. Please use a different ID.`);
        return;
      }
    }
    
    try {
      let result;
      if (editingMetadata) {
        result = await updateDashboardMetadata({

          metadataId: editingMetadata.id,
          dashboardId: metadataForm.dashboardId,
          dashboardName: metadataForm.dashboardName,
          dashboardLink: metadataForm.dashboardLink,
          description: metadataForm.description,
        });
      } else {
        result = await createDashboardMetadata({

          dashboardId: metadataForm.dashboardId,
          dashboardName: metadataForm.dashboardName,
          dashboardLink: metadataForm.dashboardLink,
          description: metadataForm.description,
        });
      }
      setMetadataModalOpen(false);
      setError(null); // Clear any previous errors
      await fetchDashboardMetadata();
      
      // Show success message with cascade update info only for updates
      if (editingMetadata && result.updatedUserAssignments > 0) {
        alert(`Dashboard updated successfully!\n\nUpdated for ${result.updatedUserAssignments} user(s).`);
      } else if (editingMetadata) {
        alert("Dashboard updated successfully!");
      }
      // No alert for dashboard creation - silent success
    } catch (err) {
      console.error("Error saving dashboard:", err);
      if (err.response?.data?.error === "Dashboard ID already exists") {
        setError(err.response.data.message || "Dashboard ID already exists. Please use a different ID.");
      } else {
        setError("Failed to save dashboard");
      }

    }
  };

  const handleDeleteMetadata = async (metadata) => {
    if (window.confirm(`Delete dashboard "${metadata.dashboardName}"?\n\nThis will also remove this dashboard from all users who have it assigned.`)) {
      try {
        const result = await deleteDashboardMetadata(metadata.id);
        await fetchDashboardMetadata();
        
        // No success alert - silent deletion after confirmation
      } catch (err) {
        setError("Failed to delete dashboard");
      }
    }
  };

  // Enhanced dropdown helpers
  const filteredDashboards = dashboardMetadata.filter(metadata =>
    metadata.dashboardName.toLowerCase().includes(dropdownSearchTerm.toLowerCase()) ||
    metadata.dashboardId.toLowerCase().includes(dropdownSearchTerm.toLowerCase())
  );

  const selectedDashboardData = dashboardMetadata.find(d => d.id === inviteForm.selectedDashboard);

  const handleDashboardSelect = (dashboardId) => {
    const isSelected = inviteForm.selectedDashboards.includes(dashboardId);
    let newSelectedDashboards;
    
    if (isSelected) {
      // Remove from selection
      newSelectedDashboards = inviteForm.selectedDashboards.filter(id => id !== dashboardId);
      // If this was the default dashboard, clear the default
      const newDefaultDashboardId = inviteForm.defaultDashboardId === dashboardId ? null : inviteForm.defaultDashboardId;
      setInviteForm({ ...inviteForm, selectedDashboards: newSelectedDashboards, defaultDashboardId: newDefaultDashboardId });
    } else {
      // Add to selection
      newSelectedDashboards = [...inviteForm.selectedDashboards, dashboardId];
      setInviteForm({ ...inviteForm, selectedDashboards: newSelectedDashboards });
    }
    
    // Keep dropdown open for multi-select
    setDropdownSearchTerm("");
  };

  const handleDefaultDashboardSelect = (dashboardId) => {
    setInviteForm({ ...inviteForm, defaultDashboardId: dashboardId });
  };

  // Check if dashboard ID is duplicate (for real-time validation)
  const isDuplicateDashboardId = (dashboardId) => {
    if (!dashboardId || editingMetadata) return false;
    return dashboardMetadata.some(
      metadata => metadata.dashboardId.toLowerCase() === dashboardId.toLowerCase()
    );
  };

  // Keyboard navigation for dropdown
  const handleKeyDown = (e) => {
    if (!dropdownOpen) return;
    
    if (e.key === 'Escape') {
      setDropdownOpen(false);
      setDropdownSearchTerm("");
    }
  };

  // Load dashboards on component mount

  useEffect(() => {
    if (adminEmail) {
      fetchDashboardMetadata();
    }
  }, [adminEmail]);

  // Close dropdown when clicking outside and handle keyboard events
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownOpen && !event.target.closest('.dropdown-container')) {
        setDropdownOpen(false);
        setDropdownSearchTerm("");
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [dropdownOpen]);


  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200/50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg">
                  <Shield className="h-6 w-6 text-white" />
                </div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent">
                 TVIQ Admin Portal
                </h1>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2 px-3 py-2 bg-slate-100 rounded-lg">
                <Mail className="h-4 w-4 text-slate-600" />
                <span className="text-sm font-medium text-slate-700">
                  {adminEmail || adminUser?.username}
                </span>
              </div>
              <Link to={"/admin/change-password"}>
                <Button variant="outline" size="sm" className="text-slate-600 border-slate-200 hover:bg-slate-50">
                  <Settings className="h-4 w-4 mr-2" />
                  Change Password
                </Button>
              </Link>
              <Button 
                variant="outline" 
                onClick={handleLogout} 
                size="sm"
                className="text-red-600 border-red-200 hover:bg-red-50"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-white/60 backdrop-blur-sm border-b border-slate-200/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            <button
              onClick={() => history.push("/admin/users")}
              className={
                "px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 " +
                (location.pathname === "/admin/users"
                  ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-sm"
                  : "text-slate-600 hover:text-slate-900 hover:bg-slate-100")
              }
            >
              Manage Users
            </button>
            <button
              onClick={() => history.push("/admin/users?tab=loginHistory")}
              className="text-slate-600 hover:text-slate-900 hover:bg-slate-100 px-3 py-2 rounded-md text-sm font-medium transition-all duration-200"
            >
              Login History
            </button>
            <button
              onClick={() => history.push("/admin/dashboard")}
              className={
                "px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 " +
                (location.pathname === "/admin/dashboard"
                  ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-sm"
                  : "text-slate-600 hover:text-slate-900 hover:bg-slate-100")
              }
            >
              Manage Dashboards
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <div className="space-y-8">
          {/* Header Section */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
            <div>
              <h2 className="text-3xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent">
                Cognito Users
              </h2>
              <p className="text-slate-600 mt-2">
                Manage Cognito users and their QuickSight registration status
              </p>
            </div>
            <div className="flex space-x-3">
              <Button 
                variant="outline" 
                onClick={handleRefresh} 
                disabled={loading}
                className="border-slate-200 text-slate-700 hover:bg-slate-50"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                {loading ? "Refreshing..." : "Refresh"}
              </Button>
            </div>
          </div>

          {/* Dashboard Management Section */}

          <Card className="bg-white/80 backdrop-blur-sm border-slate-200/50 shadow-sm">
            <CardHeader className="border-b border-slate-200/50">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
                <div className="flex items-center space-x-3">
                  <Database className="h-5 w-5 text-slate-600" />
                  <CardTitle className="text-xl font-semibold text-slate-900">
                    Dashboards

                  </CardTitle>
                  <Badge variant="secondary" className="bg-slate-100 text-slate-700">
                    {dashboardMetadata.length} dashboards
                  </Badge>
                </div>
                <Button 
                  onClick={handleCreateMetadata}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Dashboard
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              {metadataLoading ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="h-6 w-6 animate-spin text-slate-400" />
                  <span className="ml-2 text-slate-600">Loading dashboards...</span>

                </div>
              ) : dashboardMetadata.length === 0 ? (
                <div className="text-center py-8">
                  <Database className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-900 mb-2">No dashboards</h3>
                  <p className="text-slate-600 mb-4">Create dashboards to make it easier to assign them to users.</p>

                  <Button onClick={handleCreateMetadata} className="bg-blue-600 hover:bg-blue-700 text-white">
                    <Plus className="h-4 w-4 mr-2" />
                    Add First Dashboard
                  </Button>
                </div>
              ) : (
                <div className="overflow-x-auto -mx-4 px-4">
                  <div className="flex gap-3 sm:gap-4 pb-4" style={{ minWidth: 'max-content' }}>
                    {dashboardMetadata.map((metadata) => (
                      <div key={metadata.id} className="border border-slate-200 rounded-lg p-4 hover:shadow-md transition-shadow flex-shrink-0 w-72 sm:w-80">
                        <div className="flex justify-between items-start mb-3">
                          <h4 className="font-semibold text-slate-900 truncate">{metadata.dashboardName}</h4>
                          <div className="flex space-x-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditMetadata(metadata)}
                              className="h-8 w-8 p-0"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteMetadata(metadata)}
                              className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                        <div className="space-y-2 text-sm text-slate-600">
                          <div className="flex items-center space-x-2">
                            <BarChart2 className="h-4 w-4" />
                            <span className="truncate">ID: {metadata.dashboardId}</span>
                          </div>
                          <div className="flex items-start space-x-2">
                            <Link2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
                            <div className="min-w-0">
                              <span className="text-slate-600 text-sm">Link:</span>
                              <div className="text-xs text-slate-500 break-all mt-1">
                                {metadata.dashboardLink}
                              </div>
                            </div>
                          </div>
                          {metadata.description && (
                            <p className="text-slate-500 text-xs mt-2 line-clamp-2">{metadata.description}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* All Users Table */}
          <Card className="bg-white/80 backdrop-blur-sm border-slate-200/50 shadow-sm">
            <CardHeader className="border-b border-slate-200/50">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
                <div className="flex items-center space-x-3">
                  <Users className="h-5 w-5 text-slate-600" />
                  <CardTitle className="text-xl font-semibold text-slate-900">
                    Cognito Users
                  </CardTitle>
                  <Badge variant="secondary" className="bg-slate-100 text-slate-700">
                    {visibleUsers.length} total users
                  </Badge>
                  {visibleUsers.filter(u => u.quicksight?.role === 'ADMIN').length > 0 && (
                    <Badge variant="secondary" className="bg-purple-100 text-purple-700 border border-purple-200">
                      <Shield className="h-3 w-3 mr-1" />
                      {visibleUsers.filter(u => u.quicksight?.role === 'ADMIN').length} admin users
                    </Badge>
                  )}
                </div>
                <div className="flex items-center space-x-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleShowAllDashboards}
                    className={`whitespace-nowrap ${expandAllUsers ? 'bg-blue-50 border-blue-300 text-blue-700' : 'border-slate-200 text-slate-700 hover:bg-slate-50'}`}
                  >
                    <BarChart2 className="h-4 w-4 mr-2" />
                    {expandAllUsers ? "Collapse All" : "Show All Dashboards"}
                  </Button>
                  <div className="relative w-full sm:w-80">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      placeholder="Search users..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {error && (
                <div className="m-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-700">{error}</p>
                </div>
              )}

              {loading && (
                <div className="flex justify-center items-center py-12">
                  <div className="flex items-center space-x-2 text-slate-600">
                    <RefreshCw className="h-5 w-5 animate-spin" />
                    <span>Loading users...</span>
                  </div>
                </div>
              )}

              {!loading && !error && filteredUsers.length === 0 && (
                <div className="text-center py-12">
                  <Users className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                  <p className="text-slate-500 text-lg font-medium">
                    {searchTerm ? "No users found matching your search." : "No users found."}
                  </p>
                  <p className="text-slate-400 mt-2">
                    {searchTerm ? "Try adjusting your search terms." : "Create users in Cognito to get started."}
                  </p>
                </div>
              )}

              {!loading && !error && filteredUsers.length > 0 && (
                <div className="overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50/50">
                        <tr className="space-y-2">
                          <th className="text-left p-5 font-semibold text-slate-700 whitespace-nowrap">User</th>
                          <th className="text-left p-5 font-semibold text-slate-700 whitespace-nowrap">Cognito Group</th>
                          <th className="text-left p-5 font-semibold text-slate-700 whitespace-nowrap">QuickSight Status</th>
                          <th className="text-left p-5 font-semibold text-slate-700 whitespace-nowrap">QuickSight Role</th>
                          <th className="text-left p-5 font-semibold text-slate-700 whitespace-nowrap">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200/50 space-y-2">
                        {filteredUsers
                          .map((user) => (
                          <React.Fragment key={user.email}>
                          <tr className={`hover:bg-slate-50/50 transition-colors duration-150 ${
                            user.quicksight?.role === 'ADMIN' ? 'bg-purple-50/30 border-l-4 border-purple-400' : ''
                          }`}>
                            <td className="p-5 whitespace-nowrap">
                              <div className="flex items-center space-x-3">
                                <div className="flex-shrink-0">
                                  <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                                    user.quicksight?.role === 'ADMIN'
                                      ? 'bg-gradient-to-r from-purple-500 to-pink-500'
                                      : user.existsInQuickSight 
                                        ? 'bg-gradient-to-r from-blue-500 to-indigo-500' 
                                        : 'bg-gradient-to-r from-orange-500 to-red-500'
                                  }`}>
                                    <span className="text-white font-semibold text-sm">
                                      {user.email.split('@')[0].charAt(0).toUpperCase()}
                                    </span>
                                  </div>
                                </div>
                                <div>
                                  <div className="flex items-center space-x-2">
                                    <p className="font-semibold text-slate-900">{user.email.split('@')[0]}</p>
                                    
                                  </div>
                                  <p className="text-sm text-slate-600">{user.email}</p>
                                  
                                </div>
                              </div>
                            </td>

                            <td className="p-5">
                              {user.cognito?.cognitoGroup ? (
                                <Badge className={
                                  user.cognito.cognitoGroup === 'Admins'
                                    ? 'bg-blue-100 text-blue-800 border border-blue-200'
                                    : 'bg-green-100 text-green-800 border border-green-200'
                                }>
                                  {user.cognito.cognitoGroup === 'Admins' && <Shield className="h-3 w-3 mr-1" />}
                                  {user.cognito.cognitoGroup}
                                </Badge>
                              ) : (
                                <Badge className="bg-gray-100 text-gray-800 border border-gray-200">
                                  Unknown
                                </Badge>
                              )}
                            </td>

                            <td className="p-5">
                              {user.existsInQuickSight ? (
                                <Badge className="bg-green-100 text-green-800 border border-green-200">
                                  Registered
                                </Badge>
                              ) : (
                                <Badge className="bg-orange-100 text-orange-800 border border-orange-200">
                                  Not Registered
                                </Badge>
                              )}
                            </td>
                            <td className="p-5">
                              {user.quicksight?.role ? (
                                user.quicksight.role === 'ADMIN' ? (
                                  <Badge className="bg-purple-100 text-purple-800 border border-purple-200">
                                    <Shield className="h-3 w-3 mr-1" />
                                    Admin
                                  </Badge>
                                ) : (
                                  <Badge className={
                                    user.quicksight.role === 'AUTHOR'
                                      ? 'bg-green-100 text-green-800 border border-green-200'
                                      : 'bg-blue-100 text-blue-800 border border-blue-200'
                                  }>
                                    {user.quicksight.role}
                                  </Badge>
                                )
                              ) : (
                                <Badge className="bg-gray-100 text-gray-800 border border-gray-200">
                                  N/A
                                </Badge>
                              )}
                            </td>
                            <td className="p-5">
                              <div className="flex items-center space-x-2">
                                {user.quicksight?.role === 'ADMIN' ? (
                                  <div className="flex items-center space-x-2">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleShowDashboards(user)}
                                      className={`border-slate-200 hover:bg-slate-50 ${expandedUser === user.email ? 'bg-slate-100 text-slate-900' : 'text-slate-700'}`}
                                    >
                                      <BarChart2 className="h-4 w-4 mr-1" />
                                      Dashboards
                                      <ChevronDown className={`h-3 w-3 ml-1 transition-transform ${expandedUser === user.email ? 'rotate-180' : ''}`} />
                                    </Button>
                                    <Button variant="outline" size="sm" onClick={() => handleInviteToDashboard(user)} className="border-blue-200 text-blue-600 hover:bg-blue-50">
                                      <Link2 className="h-4 w-4 mr-1" />Invite
                                    </Button>
                                    <Button variant="outline" size="sm" onClick={() => handleDeleteUser(user.email)} className="border-red-200 text-red-600 hover:bg-red-50">
                                      <Trash2 className="h-4 w-4 mr-1" />Delete
                                    </Button>
                                  </div>
                                ) : user.existsInQuickSight ? (
                                  <div className="flex items-center space-x-2">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleShowDashboards(user)}
                                      className={`border-slate-200 hover:bg-slate-50 ${expandedUser === user.email ? 'bg-slate-100 text-slate-900' : 'text-slate-700'}`}
                                    >
                                      <BarChart2 className="h-4 w-4 mr-1" />
                                      Dashboards
                                      <ChevronDown className={`h-3 w-3 ml-1 transition-transform ${expandedUser === user.email ? 'rotate-180' : ''}`} />
                                    </Button>
                                    <Button variant="outline" size="sm" onClick={() => handleInviteToDashboard(user)} className="border-blue-200 text-blue-600 hover:bg-blue-50">
                                      <Link2 className="h-4 w-4 mr-1" />Invite
                                    </Button>
                                    <Button variant="outline" size="sm" onClick={() => handleDeleteUser(user.email)} className="border-red-200 text-red-600 hover:bg-red-50">
                                      <Trash2 className="h-4 w-4 mr-1" />Delete
                                    </Button>
                                  </div>
                                ) : (
                                  <Button variant="outline" size="sm" onClick={() => handleRegisterUser(user.email)} disabled={registeringUser} className="border-blue-200 text-blue-600 hover:bg-blue-50">
                                    <UserPlus className="h-4 w-4 mr-1" />
                                    {registeringUser ? "Registering..." : "Register User"}
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                          {(expandAllUsers || expandedUser === user.email) && user.existsInQuickSight && (
                            <tr className="bg-slate-50/80">
                              <td colSpan={5} className="px-8 py-4">
                                {(dashboardLoading && !userDashboards[user.email] && expandedUser === user.email) || loadingUserDashboards[user.email] ? (
                                  <div className="flex items-center space-x-2 text-slate-500 text-sm">
                                    <RefreshCw className="h-4 w-4 animate-spin" />
                                    <span>Loading dashboards...</span>
                                  </div>
                                ) : (userDashboards[user.email] || []).length === 0 ? (
                                  <p className="text-slate-500 text-sm">No dashboards assigned to this user.</p>
                                ) : (
                                  <div className="space-y-2">
                                    {(userDashboards[user.email] || []).map((dashboard) => (
                                      <div key={dashboard.id} className="flex items-center justify-between bg-white rounded-lg border border-slate-200 px-4 py-2">
                                        <div className="flex items-center space-x-3">
                                          <BarChart2 className="h-4 w-4 text-slate-400" />
                                          <span className="font-medium text-slate-800 text-sm">{dashboard.name}</span>
                                          {dashboard.isDefault && (
                                            <Badge className="bg-green-100 text-green-800 border border-green-200 text-xs">Default</Badge>
                                          )}
                                        </div>
                                        <div className="flex items-center space-x-2">
                                          {!dashboard.isDefault && (
                                            <Button variant="outline" size="sm" onClick={() => handleSetAsDefault(dashboard)} className="border-green-200 text-green-600 hover:bg-green-50 h-7 text-xs">
                                              <Settings className="h-3 w-3 mr-1" />Set Default
                                            </Button>
                                          )}
                                          <Button variant="outline" size="sm" onClick={() => handleRemoveFromDashboard(dashboard)} className="border-red-200 text-red-600 hover:bg-red-50 h-7 text-xs">
                                            <UserX className="h-3 w-3 mr-1" />Remove
                                          </Button>
                                          <a href={dashboard.link} target="_blank" rel="noopener noreferrer">
                                            <Button variant="outline" size="sm" className="border-blue-200 text-blue-600 hover:bg-blue-50 h-7 text-xs">
                                              View
                                            </Button>
                                          </a>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Invite to Dashboard Modal */}
          {inviteModalOpen && (
          <div className="fixed top-0 left-0 right-0 bottom-0 w-screen h-screen bg-black/50 backdrop-blur-sm flex items-center justify-center z-50" style={{ margin: 0, padding: 0 }}>
            <Card className="w-full max-w-2xl mx-4 h-[70vh] overflow-y-auto bg-white/95 backdrop-blur-sm border-slate-200/50 shadow-2xl">
                <CardHeader className="border-b border-slate-200/50">
                  <CardTitle className="text-xl font-bold text-slate-900 flex items-center space-x-2">
                    <Link2 className="h-5 w-5 text-blue-600" />
                    <span>Invite to Dashboard</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <form onSubmit={handleInviteSubmit} className="space-y-6">
                    <div>
                      <Label htmlFor="companyName" className="text-sm font-medium text-slate-700 mb-2 block">
                        Company Name <span className="text-slate-400 font-normal">(Optional)</span>
                      </Label>
                      <Input
                        id="companyName"
                        value={inviteForm.companyName}
                        onChange={(e) => setInviteForm({ ...inviteForm, companyName: e.target.value })}
                        placeholder="Enter company name (optional)"
                        className="w-full"
                      />
                    </div>
                    <div className="relative">
                      <Label className="text-sm font-medium text-slate-700 mb-2 block">
                        Select Dashboard
                      </Label>
                      
                      {/* Custom Dropdown Button */}
                      <div className="dropdown-container">
                      <button
                        type="button"
                        onClick={() => setDropdownOpen(!dropdownOpen)}
                        className="w-full px-4 py-3 text-left bg-white border border-slate-300 rounded-lg shadow-sm hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200 flex items-center justify-between"
                      >
                        <div className="flex items-center space-x-3">
                          <BarChart3 className="h-5 w-5 text-slate-400" />
                          <div className="flex-1">
                            {inviteForm.selectedDashboards.length > 0 ? (
                              <div>
                                <div className="font-medium text-slate-900">
                                  {inviteForm.selectedDashboards.length} dashboard{inviteForm.selectedDashboards.length > 1 ? 's' : ''} selected
                                </div>
                                <div className="text-sm text-slate-500">
                                  {inviteForm.selectedDashboards.map(id => {
                                    const metadata = dashboardMetadata.find(d => d.id === id);
                                    return metadata?.dashboardName;
                                  }).filter(Boolean).join(', ')}
                                </div>
                              </div>
                            ) : (
                              <span className="text-slate-500">Choose dashboard(s)...</span>
                            )}
                          </div>
                        </div>
                        <ChevronDown className={`h-5 w-5 text-slate-400 transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`} />
                      </button>

                      {/* Dropdown Menu */}
                      {dropdownOpen && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-96 overflow-hidden">
                          {/* Search Input */}
                          <div className="p-3 border-b border-slate-100">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium text-slate-700">Select Dashboard</span>
                              <span className="text-xs text-slate-500">
                                {filteredDashboards.length} of {dashboardMetadata.length}
                              </span>
                            </div>
                            <div className="relative">
                              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <input
                                type="text"
                                placeholder="Search dashboards..."
                                value={dropdownSearchTerm}
                                onChange={(e) => setDropdownSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                autoFocus
                              />
                            </div>
                          </div>

                          {/* Options List */}
                          <div className="max-h-80 overflow-y-auto">
                            {filteredDashboards.length === 0 ? (
                              <div className="p-4 text-center text-slate-500">
                                <BarChart3 className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                                <p className="text-sm">
                                  {dropdownSearchTerm ? 'No dashboards found' : 'No dashboards available'}
                                </p>
                                {!dropdownSearchTerm && (
                                  <p className="text-xs mt-1">Add dashboards first</p>
                                )}
                              </div>
                            ) : (
                              filteredDashboards.map((metadata) => {
                                const isSelected = inviteForm.selectedDashboards.includes(metadata.id);
                                const isDefault = inviteForm.defaultDashboardId === metadata.id;
                                return (
                                  <div
                                    key={metadata.id}
                                    className="w-full px-4 py-3 hover:bg-slate-50 focus:bg-slate-50 transition-colors duration-150 border-b border-slate-50 last:border-b-0"
                                  >
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center space-x-3">
                                        <div className="flex-shrink-0">
                                          <div 
                                            className={`w-6 h-6 rounded border-2 flex items-center justify-center cursor-pointer ${
                                              isSelected 
                                                ? 'bg-blue-600 border-blue-600' 
                                                : 'border-slate-300 hover:border-blue-400'
                                            }`}
                                            onClick={() => handleDashboardSelect(metadata.id)}
                                          >
                                            {isSelected && <Check className="h-4 w-4 text-white" />}
                                          </div>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <div className="font-medium text-slate-900 truncate">
                                            {metadata.dashboardName}
                                          </div>
                                          <div className="text-sm text-slate-500 truncate">
                                            ID: {metadata.dashboardId}
                                          </div>
                                          {metadata.description && (
                                            <div className="text-xs text-slate-400 mt-1 line-clamp-1">
                                              {metadata.description}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                      
                                    </div>
                                  </div>
                                );
                              })
                            )}
                          </div>
                          
                          {/* Done Button */}
                          {inviteForm.selectedDashboards.length > 0 && (
                            <div className="p-3 border-t border-slate-100">
                              <button
                                type="button"
                                onClick={() => setDropdownOpen(false)}
                                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200"
                              >
                                Done ({inviteForm.selectedDashboards.length} selected)
                              </button>
                            </div>
                          )}
                        </div>
                      )}

                      </div>

                    </div>
                    
                    {/* Selected Dashboards List */}
                    {inviteForm.selectedDashboards.length > 0 && (
                      <div className="mt-4">
                        <Label className="text-sm font-medium text-slate-700 mb-3 block">
                          Selected Dashboards
                        </Label>
                        <div className="space-y-2">
                          {inviteForm.selectedDashboards.map((dashboardId) => {
                            const metadata = dashboardMetadata.find(d => d.id === dashboardId);
                            const isDefault = inviteForm.defaultDashboardId === dashboardId;
                            if (!metadata) return null;
                            
                            return (
                              <div key={dashboardId} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
                                <div className="flex items-center space-x-3">
                                  <div className="flex-shrink-0">
                                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                                      <BarChart3 className="h-4 w-4 text-blue-600" />
                                    </div>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="font-medium text-slate-900 truncate">
                                      {metadata.dashboardName}
                                    </div>
                                    <div className="text-sm text-slate-500 truncate">
                                      ID: {metadata.dashboardId}
                                    </div>
                                  </div>
                                </div>
                                
                                <div className="flex items-center space-x-3">
                                  <div className="flex items-center space-x-2">
                                    <input
                                      type="radio"
                                      name="defaultDashboard"
                                      id={`default-${dashboardId}`}
                                      checked={isDefault}
                                      onChange={() => handleDefaultDashboardSelect(dashboardId)}
                                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                                    />
                                    <label 
                                      htmlFor={`default-${dashboardId}`}
                                      className="text-sm text-slate-600 cursor-pointer"
                                      onClick={() => handleDefaultDashboardSelect(dashboardId)}
                                    >
                                      Set as Default
                                    </label>
                                  </div>
                                  
                                  <button
                                    type="button"
                                    onClick={() => handleDashboardSelect(dashboardId)}
                                    className="text-red-500 hover:text-red-700 p-1"
                                    title="Remove dashboard"
                                  >
                                    <X className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    
                    <div className="flex justify-end space-x-3 pt-4">
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={() => setInviteModalOpen(false)} 
                        disabled={invitingUser}
                        className="border-slate-200 text-slate-700 hover:bg-slate-50"
                      >
                        Cancel
                      </Button>
                      <Button 
                        type="submit" 
                        disabled={invitingUser}
                        className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                      >
                        {invitingUser && (
                          <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                        )}
                        {invitingUser ? "Inviting..." : "Invite"}
                      </Button>
                    </div>
                  </form>
                  {inviteResult && inviteResult.error && (
                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-red-700 text-sm">{inviteResult.error}</p>
                    </div>
                  )}
                  {inviteResult && inviteResult.message && (
                    <div className={`mt-4 p-3 border rounded-lg ${
                      inviteResult.success 
                        ? 'bg-green-50 border-green-200' 
                        : 'bg-yellow-50 border-yellow-200'
                    }`}>
                      <p className={`text-sm ${
                        inviteResult.success 
                          ? 'text-green-700' 
                          : 'text-yellow-700'
                      }`}>
                        {inviteResult.message}
                      </p>
                      
                      {/* Show detailed results for multi-dashboard assignments */}
                      {inviteResult.results && inviteResult.results.length > 0 && (
                        <div className="mt-3 space-y-2">
                          {inviteResult.results.map((result, index) => (
                            <div key={index} className={`flex items-center space-x-2 text-xs ${
                              result.success ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {result.success ? (
                                <Check className="h-3 w-3" />
                              ) : (
                                <X className="h-3 w-3" />
                              )}
                              <span>{result.dashboardName}</span>
                              {!result.success && result.error && (
                                <span className="text-red-500">- {result.error}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Dashboard Modal */}

          {metadataModalOpen && (
            <div className="fixed top-0 left-0 right-0 bottom-0 w-screen h-screen bg-black/50 backdrop-blur-sm flex items-center justify-center z-50" style={{ margin: 0, padding: 0 }}>
              <Card className="w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto bg-white/95 backdrop-blur-sm border-slate-200/50 shadow-2xl">
                <CardHeader className="border-b border-slate-200/50">
                  <CardTitle className="text-xl font-bold text-slate-900 flex items-center space-x-2">
                    <Database className="h-5 w-5 text-blue-600" />
                    <span>{editingMetadata ? "Edit Dashboard" : "Add Dashboard"}</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  {error && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-red-700 text-sm">{error}</p>
                    </div>
                  )}

                  <form onSubmit={handleMetadataSubmit} className="space-y-6">
                    <div>
                      <Label htmlFor="metadataDashboardId" className="text-sm font-medium text-slate-700">
                        Dashboard ID
                      </Label>
                      <Input
                        id="metadataDashboardId"
                        value={metadataForm.dashboardId}
                        onChange={(e) => setMetadataForm({ ...metadataForm, dashboardId: e.target.value })}
                        required
                        placeholder="Enter dashboard ID"
                        className={`w-full ${isDuplicateDashboardId(metadataForm.dashboardId) ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                      />
                      {isDuplicateDashboardId(metadataForm.dashboardId) && (
                        <p className="text-red-600 text-sm mt-1">
                          This dashboard ID already exists. Please use a different ID.
                        </p>
                      )}

                    </div>
                    <div>
                      <Label htmlFor="metadataDashboardName" className="text-sm font-medium text-slate-700">
                        Dashboard Name
                      </Label>
                      <Input
                        id="metadataDashboardName"
                        value={metadataForm.dashboardName}
                        onChange={(e) => setMetadataForm({ ...metadataForm, dashboardName: e.target.value })}
                        required
                        placeholder="Enter dashboard name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="metadataDashboardLink" className="text-sm font-medium text-slate-700">
                        Dashboard Link
                      </Label>
                      <Input
                        id="metadataDashboardLink"
                        value={metadataForm.dashboardLink}
                        onChange={(e) => setMetadataForm({ ...metadataForm, dashboardLink: e.target.value })}
                        required
                        placeholder="Enter dashboard link"
                      />
                    </div>
                    <div>
                      <Label htmlFor="metadataDescription" className="text-sm font-medium text-slate-700">
                        Description (Optional)
                      </Label>
                      <textarea
                        id="metadataDescription"
                        value={metadataForm.description}
                        onChange={(e) => setMetadataForm({ ...metadataForm, description: e.target.value })}
                        placeholder="Enter dashboard description"
                        className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                        rows={3}
                      />
                    </div>
                    <div className="flex justify-end space-x-3 pt-4">
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={() => setMetadataModalOpen(false)} 
                        className="border-slate-200 text-slate-700 hover:bg-slate-50"
                      >
                        Cancel
                      </Button>
                      <Button 
                        type="submit" 
                        disabled={isDuplicateDashboardId(metadataForm.dashboardId)}
                        className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 disabled:from-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed"

                      >
                        {editingMetadata ? "Update" : "Create"}
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminQuickSight; 